import { useContext } from 'react'
import { useManualQuery } from 'graphql-hooks'

import * as logger from '../../../../../../services/logger'
import { postCoreEvent } from '../../../../../../services/core-event'
import { notificationStore } from '../../../../../../context/notificationsContext/store'
import { getLocationUserIds } from '../../../../../../graphql/custom-queries'
import { IGetLocationGeneral } from '../../../../../../types/locations'
import { IPlace } from '@vacationtracker/shared/data/countries'
import { IPlaceHolidays } from '../../../../../../util/location-facts'
import { buildHolidaysImportedPayload, buildLocationUpdatedPayload, buildLocationWorkWeekPayload, IWorkWeekChange, TLocationGeneralPatch } from './location-event-payloads'

export interface IUseLocationFieldSaveParams {
  location: IGetLocationGeneral
  onPatch: (patch: Partial<IGetLocationGeneral>) => void
}

export interface IUseLocationFieldSave {
  saveFields: (patch: TLocationGeneralPatch, options?: { track?: boolean }) => Promise<void>
  saveWorkWeek: (next: IWorkWeekChange) => Promise<void>
  importHolidays: (place: IPlace, placeHolidays: IPlaceHolidays, locale: string) => Promise<void>
}

/**
 * Saving one field of a location, optimistically.
 *
 * The page refetches a second after any event notification settles, so without the optimistic patch
 * every edit would show the old value again for about a second before landing. The inverse patch on
 * failure mirrors useOptimisticUserEvent, and the error is rethrown so the row can show it.
 *
 * Per-field saves do not register their correlation id: the notification container
 * already surfaces failures on `code === 'error'` whether or not the id is tracked, so registering
 * would only add a success toast whose View button points at the page you are already on, plus a
 * refetch the optimistic patch has made unnecessary. Explicit, consequential actions - making a
 * location default, importing holidays - do register, and pass `track`.
 */
export function useLocationFieldSave({ location, onPatch }: IUseLocationFieldSaveParams): IUseLocationFieldSave {
  const { addActionNotifications } = useContext(notificationStore)
  const [getLocationUserIdsQuery] = useManualQuery<{ getLocation: { users: { id: string }[] } }, { id: string }>(getLocationUserIds)

  /**
   * The membership as it stands now, not as this page last rendered it. Every LOCATION_UPDATED
   * carries the full list and the handler moves anyone missing to the default location, so a save
   * made just after somebody was added on the Users tab would otherwise move them straight back out.
   * Falls back to the rendered list when the read fails - sending a slightly stale list is a risk,
   * sending none at all is a rejected event.
   */
  const currentUserIds = async (): Promise<string[]> => {
    try {
      const response = await getLocationUserIdsQuery({ variables: { id: location.id } })
      const users = response.data?.getLocation.users
      return users ? users.map((user) => user.id) : location.users.map((user) => user.id)
    } catch (error) {
      logger.warning({ errorType: 'ERROR READ LOCATION MEMBERS BEFORE SAVE', error })
      return location.users.map((user) => user.id)
    }
  }

  const saveFields = async (patch: TLocationGeneralPatch, options?: { track?: boolean }) => {
    const inverse = Object.keys(patch).reduce(
      (acc, key) => {
        acc[key] = location[key]
        return acc
      },
      {} as Record<string, unknown>
    ) as Partial<IGetLocationGeneral>

    onPatch(patch)
    try {
      const response = await postCoreEvent(buildLocationUpdatedPayload(location, patch, await currentUserIds()), 'location-general')
      if (options?.track) {
        addActionNotifications([response.correlationId])
      }
    } catch (error) {
      onPatch(inverse)
      logger.warning({ errorType: 'ERROR INLINE LOCATION EDIT', error })
      throw error
    }
  }

  const saveWorkWeek = async (next: IWorkWeekChange) => {
    const inverse: Partial<IGetLocationGeneral> = { workWeek: location.workWeek, workHours: location.workHours }

    // Patch from the payload rather than recomputing the sorted days and hour pairs: two
    // derivations of the same thing is how the optimistic value and the posted one drift apart.
    const payload = buildLocationWorkWeekPayload(location.id, next)
    const patch: Partial<IGetLocationGeneral> = { workWeek: payload.days, workHours: payload.workHours ?? undefined }

    // This is the one per-field save whose event reaches past the fields it writes: overriding
    // everyone deletes each person's own work week, one USER_WORK_WEEK_DELETED per user. The count
    // of people who still have one is read off this list, so patching only the days would leave the
    // row insisting they do until the page was reloaded.
    if (payload.cancelUserSettings) {
      inverse.users = location.users
      patch.users = location.users.map((user) => ({ ...user, workWeekType: 'LOCATION' }))
    }

    onPatch(patch)
    try {
      const response = await postCoreEvent(payload, 'location-general')
      // And the one that registers its correlation id: those per-user deletions settle after this
      // event does, so the refetch is what swaps the assumed membership for the real one.
      if (payload.cancelUserSettings) {
        addActionNotifications([response.correlationId])
      }
    } catch (error) {
      onPatch(inverse)
      logger.warning({ errorType: 'ERROR INLINE LOCATION WORK WEEK EDIT', error })
      throw error
    }
  }

  /**
   * The place is a parameter, not read off `location`: the only caller imports straight after
   * saving a new country, and `location` is still the render's value at that point. The stamped
   * country is what the January cron replays, so a stale one re-imports the wrong country a year
   * later - and on a location that had no country it is `undefined`, which the schema rejects.
   */
  const importHolidays = async (place: IPlace, placeHolidays: IPlaceHolidays, locale: string) => {
    const response = await postCoreEvent(buildHolidaysImportedPayload({ locationId: location.id, place, placeHolidays, locale }), 'location-general')
    addActionNotifications([response.correlationId])
  }

  return { saveFields, saveWorkWeek, importHolidays }
}
